import type { Explanation } from '../../explanations/types';
import { commonScopes, type Translate } from '../../explanations/common';
import type { TipoRelatorioOS } from '../../composables/useRelatoriosOS';

/**
 * The OS reports explain their numbers from here (skill `self-explaining-ui`).
 * Every sentence describes Relatorios_os_model in app-api — the rules live
 * there: a SALE is a PAID OS (received ≥ total), neither cancelled nor draft,
 * opened in the period; production steps count by their completion date.
 * Consumption per client (NS 2026-0104) counts only paid OS with a service.
 */
export type OsReportExplanations = Record<TipoRelatorioOS, Record<string, Explanation>>;

export const osReportExplanations = (t: Translate): OsReportExplanations => {
  const SCOPE = commonScopes(t);
  const money = `${SCOPE.franchiseFilterAndPeriod} ${SCOPE.localCurrency}`;

  return {
    clientes: {
      ordens: {
        what: t('Quantas OS quitadas deste cliente foram abertas no período.'),
        now: { kind: 'info', text: t('Quitada: o recebido cobre o total. Cancelada e rascunho não contam.') },
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      total: {
        what: t('Quanto o cliente pagou nas OS quitadas do período.'),
        calculation: [t('Total de cada OS quitada'), t('− desconto da OS (já descontado)')],
        scope: money,
      },
      ticket_medio: {
        what: t('Quanto o cliente gastou, em média, por OS.'),
        calculation: [t('Total'), t('÷ OS')],
        scope: money,
      },
    },
    servicos: {
      quantidade: {
        what: t('Unidades deste serviço vendidas em OS quitadas do período.'),
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      ordens: {
        what: t('Em quantas OS quitadas o serviço apareceu.'),
        now: { kind: 'info', text: t('Uma OS com dois serviços conta nos dois: a soma da coluna passa do total de OS.') },
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      valor: {
        what: t('Soma dos itens deste serviço nas OS quitadas, antes do desconto da OS.'),
        calculation: [t('Valor de cada item do serviço')],
        now: { kind: 'info', text: t('O desconto é da OS inteira e não entra aqui — é a mesma soma do gráfico por categoria do Dashboard.') },
        scope: money,
      },
      valor_medio: {
        what: t('Preço médio de cada unidade vendida.'),
        calculation: [t('Valor'), t('÷ Qtd')],
        scope: money,
      },
    },
    consumo: {
      ordens: {
        what: t('Quantas OS quitadas com serviço este cliente abriu no período.'),
        now: { kind: 'info', text: t('Quitada: o recebido cobre o total. Cancelada e rascunho não contam, nem OS só de produto.') },
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      faixa_recorrencia: {
        what: t('Em que faixa de recorrência o cliente está: 1, 2 a 3, ou 4 ou mais OS no período.'),
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      servicos: {
        what: t('Quantos serviços o cliente consumiu: serviços e adicionais, pela quantidade.'),
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      pares: {
        what: t('Quantos pares passaram pelos serviços.'),
        calculation: [t('Quantidade dos itens de serviço')],
        now: { kind: 'info', text: t('O adicional é feito no mesmo par e não conta outro par.') },
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      valor: {
        what: t('Quanto o cliente consumiu em serviços, adicionais e combos, antes do desconto da OS.'),
        calculation: [t('Itens de serviço'), t('+ adicionais'), t('+ venda de combo')],
        now: { kind: 'info', text: t('Produto não entra. O par coberto por combo vem com valor 0; o dinheiro está na venda do combo.') },
        scope: money,
      },
      ticket_medio: {
        what: t('Quanto o cliente consumiu, em média, por OS.'),
        calculation: [t('Valor'), t('÷ OS')],
        scope: money,
      },
      valor_por_par: {
        what: t('Quanto o cliente pagou, em média, por par.'),
        calculation: [t('Valor'), t('÷ Pares')],
        now: { kind: 'info', text: t('Traço quando não houve par no período (só combo ou adicional).') },
        scope: money,
      },
      intervalo_medio_dias: {
        what: t('De quantos em quantos dias, em média, o cliente voltou.'),
        calculation: [t('Última − primeira OS, em dias'), t('÷ (OS − 1)')],
        now: { kind: 'info', text: t('Traço com uma OS só: não há intervalo.') },
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      dias_desde_ultimo: {
        what: t('Quantos dias se passaram de hoje até a última OS do período.'),
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      recorrentes: {
        what: t('Quantos clientes da lista voltaram: 2 OS ou mais no período.'),
        calculation: [t('Clientes com 2 OS ou mais'), t('÷ clientes da lista')],
        scope: SCOPE.franchiseFilterAndPeriod,
      },
    },
    produtividade: {
      ordens: {
        what: t('OS quitadas que esta pessoa abriu no período.'),
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      valor: {
        what: t('Soma das OS quitadas que esta pessoa abriu, já com o desconto da OS.'),
        calculation: [t('Total de cada OS quitada que ela abriu')],
        scope: money,
      },
      ticket_medio: {
        what: t('Valor médio das OS que esta pessoa abriu.'),
        calculation: [t('Valor das OS'), t('÷ OS abertas')],
        scope: money,
      },
      etapas: {
        what: t('Etapas de produção que esta pessoa concluiu, de qualquer OS — quitada ou não.'),
        now: { kind: 'info', text: t('Aqui o período é a data em que a etapa foi concluída, não a de abertura da OS.') },
        scope: SCOPE.franchiseFilterAndPeriod,
      },
      itens: {
        what: t('Em quantos itens diferentes essas etapas foram feitas.'),
        now: { kind: 'info', text: t('Três etapas no mesmo item contam como um item.') },
        scope: SCOPE.franchiseFilterAndPeriod,
      },
    },
  };
};
